import React, { useState } from 'react'
import type { TenantThemeConfig } from '../types/theme'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernHowtoPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

/** ข้อมูล accordion คู่มือ — ตรง howto.html */
const HOWTO_SECTIONS: Array<{ id: string; title: string; steps: string[] }> = [
  {
    id: 'one',
    title: 'สมัครสมาชิก',
    steps: [
      'กรอกข้อมูลของท่านลงในช่องว่างหมายเลขโทรศัพท์',
      'กำหนดรหัสผ่านที่ท่านต้องการใช้ในการเข้าสู่ระบบเพื่อทำรายการ ฝาก-ถอน',
      'กรอก ชื่อ-นามสกุล ของท่าน',
      'กรอกหมายเลขบัญชีธนาคารของท่าน',
      'เลือกธนาคารที่ท่านต้องการจะใช้ในการทำรายการกับเว็บของเรา',
      'กรอก ID LINE',
      'เลือกช่องทางที่ท่านรุ้จักเรา',
    ],
  },
  {
    id: 'two',
    title: 'ทำรายการฝากเงิน',
    steps: [
      'เข้าหน้าเว็บเลือก "เข้าสู่ระบบ" กรอก User และ Password แล้วกด "ล็อกอิน"',
      'เมื่อเข้าสู่ระบบเรียบร้อยแล้วเลือกที่เมนู "ฝากเงิน"',
      'โอนเงินตามหมายเลขบัญชีที่ทางเว็บระบุไว้',
      'เมื่อโอนเงินเรียบร้อยแล้วรอ 30 วินาที เครดิตจะเข้า User ของท่านโดยอัตโนมัติ',
      'ท่านสามารถเข้าเล่นได้ทันที',
    ],
  },
  {
    id: 'three',
    title: 'ทำรายการถอนเงิน',
    steps: [
      'เข้าหน้าเว็บเลือก "เข้าสู่ระบบ" กรอก User และ Password แล้วกด "ล็อกอิน"',
      'เมื่อเข้าสู่ระบบเรียบร้อยแล้วเลือกที่เมนู "ถอนเงิน"',
      'กรอกจำนวนเงินที่ท่านต้องการถอน',
      'หลังจากกรอกจำนวนเงินแล้วคลิกที่ "ดำเนินการ"',
      '***จำนวนเงินขั้นต่ำในการแจ้งถอนต้องมี 300 บาท ขึ้นไป***',
    ],
  },
  {
    id: 'four',
    title: 'การรับโบนัส',
    steps: [
      'เข้าหน้าเว็บเลือก "เข้าสู่ระบบ" กรอก User และ Password แล้วกด "ล็อกอิน"',
      'เมื่อเข้าสู่ระบบเรียบร้อยแล้วเลือกที่เมนู "โบนัส"',
      'ทุกโบนัสต้องกดรับหลังจากฝากเงินเท่านั้นห้ามเล่นก่อนเด็ดขาด',
      '***หากทีมงานตรวจสอบว่ามีการรับโบนัสเพื่อแทงสวน ทางทีมงานจะไม่ทำรายการถอนให้ทุกกรณี***',
    ],
  },
  {
    id: 'five',
    title: 'การแนะนำเพื่อน',
    steps: [
      'เข้าหน้าเว็บเลือก "เข้าสู่ระบบ" กรอก User และ Password แล้วกด "ล็อกอิน"',
      'เมื่อเข้าสู่ระบบเรียบร้อยแล้วเลือกที่เมนู "แนะนำเพื่อน"',
      'กดปุ่มคัดลอกลิงค์ในช่องลิงค์แนะนำเพื่อนเพื่อนำไปแนะนำเพื่อน เมื่อเพื่อนสมัครผ่านลิงค์ท่านจะได้รับ 0.6 % จากยอดเทิร์นโอเวอร์ของเพื่อน',
      'แนะนำได้ไม่จำกัดจำนวน ยิ่งแนะนำเยอะ ยิ่งได้เยอะ',
    ],
  },
  {
    id: 'six',
    title: 'การรับเครดิตคืน',
    steps: [
      'เข้าหน้าเว็บเลือก "เข้าสู่ระบบ" กรอก User และ Password แล้วกด "ล็อกอิน"',
      'เมื่อเข้าสู่ระบบเรียบร้อยแล้วเลือกที่เมนู "รับเครดิตคืน"',
      'กดปุ่มเพื่อรับเครดิตฟรีคืนท่านต้องมียอดการเล่นมากกว่า 15 บาท',
      'แนะนำให้เงื่อนไขกับกติกาให้ครบถ้วยด้วย',
    ],
  },
]

/** หน้าคู่มือ — แปลงจาก howto.html (accordion สมัครสมาชิก, ฝากเงิน, ถอนเงิน, โบนัส, แนะนำเพื่อน, เครดิตคืน) */
const ModernHowtoPage: React.FC<ModernHowtoPageProps> = () => {
  const [openId, setOpenId] = useState<string | null>('one')

  return (
    <div className="content">
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="c_quinary text-center">
              <h3 className="c_tertiary font_md">คู่มือ</h3>
            </div>

            <div className="accordion acd_howto d-grid gap-1 mt-3" id="accordionExample">
              {HOWTO_SECTIONS.map((section) => {
                const isOpen = openId === section.id
                return (
                  <div key={section.id} className="accordion-item">
                    <h2 className="accordion-header" id={`heading-${section.id}`}>
                      <button
                        type="button"
                        className={`accordion-button${isOpen ? '' : ' collapsed'}`}
                        aria-expanded={isOpen}
                        aria-controls={`collapse-${section.id}`}
                        onClick={() => setOpenId(isOpen ? null : section.id)}
                      >
                        {section.title}
                      </button>
                    </h2>
                    <div
                      id={`collapse-${section.id}`}
                      className={`accordion-collapse collapse${isOpen ? ' show' : ''}`}
                      aria-labelledby={`heading-${section.id}`}
                      data-bs-parent="#accordionExample"
                    >
                      <div className="accordion-body">
                        <ol>
                          {section.steps.map((step, i) => (
                            <li key={i}>
                              <h6>{step}</h6>
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModernHowtoPage
